// components/entities/pages/DealPage.tsx
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import clsx from 'clsx';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { DEFAULT_AUTOSAVE_STATE, useEntityDraftAutosave } from '@/kernel/useEntityDraftAutosave';
import {
  AutoSaveAdapter,
  DealEconomicsTab,
  DealInventoryEntityState,
  DealRuntimeState,
  ContactDTO,
  LEASING_ACTION_TYPES,
  LeasingActionType,
  LeasingDealDTO,
} from '#types/types';
import { EntitySkeleton } from '../EntityView';
import { EntityPageTabs } from '@/components/portal/EntityPageTabs';
import { UnitPage } from './UnitPage';
import { GenerateIncentiveLetterActionPanel } from '@/components/panels/GenerateIncentiveLetterActionPanel';
import {
  AlertTriangle,
  AtSign,
  Briefcase,
  FileText,
  Hash,
  Mail,
  Phone,
  ShieldAlert,
  Trash2,
  Type,
} from '@/components/ui/lucide-memo';
import { UnitThumbnail } from '@/components/entities/UnitThumbnail';
import Switch from '@/components/ui/Switch';
import { ProjectsGrid } from '../grids/ProjectsGrid';
import { computeDealIncentiveEnvelope } from '#lib/leasing/dealIncentiveEnvelope';

const translations = {
  en: {
    designWorkspaceTitle: 'Unit Visual Input',
    designWorkspaceHint: 'Upload or paste a unit image, then launch a Studio quickstart for this deal.',
    quickstartDesign: 'Quickstart Design',
    quickstartDesignDisabled: 'Add a unit thumbnail to unlock quickstart',
    unknownName: 'Unknown',
    unknownRole: 'Unknown role',
    notAvailable: '\u2014',
    targetBadge: 'Target',
    noContactsOnBrand: 'No contacts on this brand.',
    runDiscoveryDesc: 'Run discovery to fetch expansion and real-estate decision makers for this brand.',
    useAsIncentiveTarget: 'Use as Incentive Target',
    placeholderName: 'Jane Doe',
    placeholderRole: 'Head of Expansion',
    placeholderEmail: 'expansion@brand.com',
    placeholderPhone: '+33 6 00 00 00 00',
    contactSaved: 'Contact saved',
    saveFailed: 'Save failed',
    unsavedChanges: 'Unsaved changes',
    upToDate: 'Up to date',
    saving: 'Saving...',
    invalidEmail: 'Enter a valid email address.',
    brandContacts: 'Brand Contacts',
    discovering: 'Discovering...',
    discoverContacts: 'Discover Contacts',
    contactNameLabel: 'Name',
    contactRoleLabel: 'Title / Role',
    contactEmailLabel: 'Email *',
    contactPhoneLabel: 'Phone',
    initializingFinancialEngine: 'Initializing Financial Engine...',
    foundationRequired: 'Foundation Required',
    foundationRequiredDesc: 'Set base rent and firm commitment period first. Total legal duration is optional; when empty, economics falls back to the firm period.',
    facialBaseRent: 'Facial Base Rent',
    perSqm: '€/m²',
    rentFreeConcession: 'Rent-Free Concession',
    tenantFitOutEstimate: 'Tenant Fit-Out Estimate',
    fitOutContribution: 'Fit-Out Contribution',
    highRisk: 'High Risk:',
    progressiveRent: 'Progressive Rent (Steps)',
    addProgressionStep: '+ Add Progression Step',
    jumpWarning: (prev: number, curr: number, increase: number) => `Jump from ${prev}% to ${curr}% is a +${increase}% increase (High default risk).`,
    assetValuation: 'Asset Valuation',
    assumingExitYield: (yieldPct: number) => `Assuming ${yieldPct}% Exit Yield`,
    netEffectiveRent: 'Net Effective Rent',
    totalIncentives: 'Total Incentives',
    capexOpexCombined: 'CAPEX & OPEX Combined',
    paybackPeriod: 'Payback Period',
    exceedsFirmPeriod: 'Exceeds Firm Period!',
    discountEquivalent: 'Discount Equivalent',
    contactDiscoveryLaunched: 'Contact discovery launched. Results should populate this list shortly.',
    contactDiscoveryFailed: 'Contact discovery failed. Check brand data and retry.',
    loadingContactWorkspace: 'Loading Contact Workspace...',
    firmTotalYears: (firm: number, total: number) => `Firm: ${firm}y / Total: ${total}y`,
  },
  fr: {
    designWorkspaceTitle: 'Visuel unité',
    designWorkspaceHint: 'Uploadez ou collez une image d\'unité, puis lancez un quickstart Studio pour ce deal.',
    quickstartDesign: 'Quickstart Design',
    quickstartDesignDisabled: 'Ajoutez une vignette unité pour activer le quickstart',
    unknownName: 'Inconnu',
    unknownRole: 'Rôle inconnu',
    notAvailable: '\u2014',
    targetBadge: 'Cible',
    noContactsOnBrand: 'Aucun contact pour cette marque.',
    runDiscoveryDesc: 'Lancez la découverte pour récupérer les décideurs immobiliers et d\'expansion pour cette marque.',
    useAsIncentiveTarget: 'Utiliser comme cible',
    placeholderName: 'Jeanne Dupont',
    placeholderRole: 'Responsable Expansion',
    placeholderEmail: 'expansion@marque.com',
    placeholderPhone: '+33 6 00 00 00 00',
    contactSaved: 'Contact enregistré',
    saveFailed: 'Échec de l\'enregistrement',
    unsavedChanges: 'Modifications non enregistrées',
    upToDate: 'À jour',
    saving: 'Enregistrement...',
    invalidEmail: 'Saisissez une adresse e-mail valide.',
    brandContacts: 'Contacts de la marque',
    discovering: 'Découverte...',
    discoverContacts: 'Découvrir les contacts',
    contactNameLabel: 'Nom',
    contactRoleLabel: 'Titre / Rôle',
    contactEmailLabel: 'E-mail *',
    contactPhoneLabel: 'Téléphone',
    initializingFinancialEngine: 'Initialisation du moteur financier...',
    foundationRequired: 'Fondation requise',
    foundationRequiredDesc: 'Définissez d\'abord le loyer de base et la période d\'engagement ferme. La durée légale totale est optionnelle : si elle est vide, les calculs utilisent la période ferme.',
    facialBaseRent: 'Loyer de base facial',
    perSqm: '€/m²',
    rentFreeConcession: 'Concession de franchise de loyer',
    tenantFitOutEstimate: 'Estimation aménagement locataire',
    fitOutContribution: 'Contribution à l\'aménagement',
    highRisk: 'Risque élevé :',
    progressiveRent: 'Loyer progressif (Paliers)',
    addProgressionStep: '+ Ajouter un palier de progression',
    jumpWarning: (prev: number, curr: number, increase: number) => `Le saut de ${prev}% à ${curr}% représente une augmentation de +${increase}% (Risque de défaut élevé).`,
    assetValuation: 'Valorisation de l\'actif',
    assumingExitYield: (yieldPct: number) => `Hypothèse de rendement de sortie : ${yieldPct}%`,
    netEffectiveRent: 'Loyer effectif net',
    totalIncentives: 'Incitations totales',
    capexOpexCombined: 'CAPEX & OPEX combinés',
    paybackPeriod: 'Période de récupération',
    exceedsFirmPeriod: 'Dépasse la période ferme !',
    discountEquivalent: 'Équivalent de remise',
    contactDiscoveryLaunched: 'Découverte des contacts lancée. Les résultats devraient apparaître dans cette liste sous peu.',
    contactDiscoveryFailed: 'Échec de la découverte des contacts. Vérifiez les données de la marque et réessayez.',
    loadingContactWorkspace: 'Chargement de l\'espace contact...',
    firmTotalYears: (firm: number, total: number) => `Ferme : ${firm}a / Total : ${total}a`,
  }
};

type DealTranslations = (typeof translations)[keyof typeof translations];

function DealDesignsTab({ dealId }: { dealId: string }) {
  const { kernel } = useKernel();
  const lang = useKernelStore((state) => state.language);
  const t = translations[lang];
  const deal = useKernelStore((state) => state.inventory.Deal.registry[dealId]) as DealInventoryEntityState | undefined;
  const unitRegistry = useKernelStore((state) => state.inventory.Unit.registry);
  const brandRegistry = useKernelStore((state) => state.inventory.Brand.registry);
  const isFaterOrgMember = useKernelStore((state) => state.userContext.isFaterOrgMember);
  const isQuickstarting =
    deal?.designsIsQuickstarting === true || deal?.runtime?.designsIsQuickstarting === true;
  const setIsQuickstarting = useCallback((value: boolean) => {
    if (!kernel.current) return;
    kernel.current.patchEntityRuntimeState(
      'Deal',
      dealId,
      { designsIsQuickstarting: value }
    );
  }, [kernel, dealId]);

  const unit = deal?.unitId ? unitRegistry[deal.unitId] : null;
  const brand = deal?.brandId ? brandRegistry[deal.brandId] : null;
  
  const thumbnailId = unit?.thumbnailFileId || null;
  const thumbnailUrl = thumbnailId ? `/api/inventory/assets/${thumbnailId}` : null;

  const associatedProjectIds = useMemo(() => {
    if (!Array.isArray(deal?.projectIds) || deal.projectIds.length === 0) return [];
    return Array.from(new Set(deal.projectIds.filter((projectId): projectId is string => typeof projectId === 'string' && projectId.length > 0)));
  }, [deal?.projectIds]);

  const lockedProjectFilters = useMemo(
    () => ({
      ids:
        associatedProjectIds.length > 0
          ? associatedProjectIds
          : [`__deal-${dealId}-no-linked-project__`],
    }),
    [associatedProjectIds, dealId]
  );

  useEffect(() => {
    if (!kernel.current || !deal?.unitId || unit) return;
    void kernel.current.syncEntity('Unit', deal.unitId);
  }, [kernel, deal?.unitId, unit]);

  const handleQuickstart = useCallback(async () => {
    if (!kernel.current || !deal || !thumbnailUrl || isQuickstarting) return;
    setIsQuickstarting(true);
    try {
      const brandName = brand?.name || 'target brand';
      const prompt = `Rebrand this space into a ${brandName}.`;
      await kernel.current.startDealProject({
        dealId: deal.id,
        prompt,
        unitThumbnail: thumbnailUrl,
        brandLogo: brand?.logoUrl,
      });
    } finally {
      setIsQuickstarting(false);
    }
  }, [kernel, deal, brand, thumbnailUrl, isQuickstarting, setIsQuickstarting]);

  const handleQuickstartClick = useCallback(() => {
    void handleQuickstart();
  }, [handleQuickstart]);

  const dealUnitId = deal?.unitId;
  const handleThumbnailUploaded = useCallback(() => {
    if (!kernel.current || !dealUnitId) return;
    void kernel.current.syncEntity('Unit', dealUnitId);
  }, [kernel, dealUnitId]);

  if (!deal) {
    return <EntitySkeleton variant="detail" />;
  }

  return (
    <div className="h-full w-full p-6 flex flex-col gap-6">
      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
        <div className="text-xs text-white/40 mb-3">{t.designWorkspaceTitle}</div>
        <div className="flex flex-col lg:flex-row gap-5 lg:items-start">
          <UnitThumbnail
            unitId={deal.unitId}
            isEditable={isFaterOrgMember}
            className="w-full lg:w-[28rem] max-w-full aspect-video border border-white/10"
            onUploaded={handleThumbnailUploaded}
          />
          <div className="flex flex-col gap-3 w-full">
            <p className="text-sm text-white/60">{t.designWorkspaceHint}</p>
            {thumbnailUrl ? (
              <button
                type="button"
                onClick={handleQuickstartClick}
                disabled={isQuickstarting}
                className={clsx(
                  'self-start px-4 py-2 rounded-lg border text-sm transition-colors',
                  isQuickstarting
                    ? 'border-white/10 bg-white/5 text-white/30 cursor-not-allowed'
                    : 'border-white/20 bg-white/10 text-white hover:bg-white/15'
                )}
              >
                {isQuickstarting ? `${t.quickstartDesign}...` : t.quickstartDesign}
              </button>
            ) : (
              <p className="text-xs text-white/35">{t.quickstartDesignDisabled}</p>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-2 flex-1 min-h-[220px]">
        <ProjectsGrid
          lockedFilters={lockedProjectFilters}
          showDefaultToolbarSelectors={false}
        />
      </div>
    </div>
  );
}

const DEFAULT_CAP_RATE = 0.05; // 5% Exit Yield assumption for valuation
const ECONOMICS_AUTOSAVE_DEBOUNCE_MS = 1000;
const CONTACT_AUTOSAVE_DEBOUNCE_MS = 900;
const SQFT_TO_SQM = 0.09290304;
const DEAL_CONTACT_AUTOSAVE_SCOPE = 'deal_contact';
const DEAL_ECONOMICS_AUTOSAVE_SCOPE = 'deal_economics';

type DealEconomicsDraftPayload = {
  rentAmount: number;
  leaseFirmPeriodYears: number;
  leaseDurationYears: number;
  incentiveRentFreeMonths: number;
  incentiveFitOutAmount: number;
  estimatedTenantFitoutCost: number | null;
  estimatedTenantFitoutCostPerSqm: number | null;
  incentiveHasStepRents: boolean;
  incentiveSteps: Array<{
    stepOrder: number;
    startMonth: number;
    endMonth: number;
    rentPercent: number;
  }>;
};

const serializeEconomicsPayload = (payload: DealEconomicsDraftPayload): string => JSON.stringify(payload);
type DealContactDraftPayload = {
  name: string | null;
  email: string | null;
  phone: string | null;
  role: string | null;
  company: string | null;
};
type DealContactSaveState = 'idle' | 'saved' | 'error';
type DealEconomicsStepDraft = { startMonth: number; endMonth: number; rentPercent: number };
type DealEconomicsRuntimeDraftPatch = Partial<
  Pick<
    DealRuntimeState,
    | 'economicsDraftInitializedDealId'
    | 'economicsRentAmountDraft'
    | 'economicsLeaseFirmPeriodYearsDraft'
    | 'economicsLeaseDurationYearsDraft'
    | 'economicsIncentiveRentFreeMonthsDraft'
    | 'economicsIncentiveFitOutAmountDraft'
    | 'economicsEstimatedTenantFitoutCostDraft'
    | 'economicsIncentiveHasStepRentsDraft'
    | 'economicsIncentiveStepsDraft'
  >
>;

const serializeContactPayload = (payload: DealContactDraftPayload): string => JSON.stringify(payload);

const normalizeContactField = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const extractDealContactCompany = (deal?: LeasingDealDTO, fallbackCompany?: string | null): string => {
  if (!deal) return fallbackCompany ?? '';
  if (typeof deal.contactCompany === 'string' && deal.contactCompany.trim().length > 0) {
    return deal.contactCompany;
  }
  const info = deal.contactInfo;
  if (info && typeof info === 'object' && !Array.isArray(info)) {
    const candidate = (info as Record<string, unknown>).company;
    if (typeof candidate === 'string') {
      return candidate;
    }
  }
  return fallbackCompany ?? '';
};

const extractDealContactRole = (deal?: LeasingDealDTO): string => {
  if (!deal) return '';
  const info = deal.contactInfo;
  if (info && typeof info === 'object' && !Array.isArray(info)) {
    const roleCandidate = (info as Record<string, unknown>).role;
    if (typeof roleCandidate === 'string') {
      return roleCandidate;
    }
    const titleCandidate = (info as Record<string, unknown>).title;
    if (typeof titleCandidate === 'string') {
      return titleCandidate;
    }
  }
  return '';
};

const extractRoleFromContactInfo = (value: unknown): string => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return '';
  const info = value as Record<string, unknown>;
  if (typeof info.role === 'string') return info.role;
  if (typeof info.title === 'string') return info.title;
  return '';
};

const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const parsePositiveNumber = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? value : null;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().replace(',', '.');
    if (!normalized) return null;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }
  return null;
};

const CONTACT_GRID = 'grid grid-cols-[36px_1fr_1fr_1.2fr_0.8fr_80px] gap-0 items-center';
const CONTACT_HEADER_FONT_STYLE = { fontFamily: 'ui-sans-serif, -apple-system, system-ui, sans-serif' };

interface DealContactRowProps {
  contact: ContactDTO;
  name: string;
  currentTargetEmail: string | null;
  isSavingContact: boolean;
  t: DealTranslations;
  onUseAsTarget: (contact: ContactDTO) => void;
}

const DealContactRow = React.memo(function DealContactRow({
  contact,
  name,
  currentTargetEmail,
  isSavingContact,
  t,
  onUseAsTarget,
}: DealContactRowProps) {
  const isCurrentTarget = Boolean(currentTargetEmail && normalizeContactField(contact.email ?? '') === currentTargetEmail);
  const phone = contact.directPhone || contact.primaryPhone || '';

  const handleUseAsTarget = useCallback(() => {
    onUseAsTarget(contact);
  }, [onUseAsTarget, contact]);

  return (
    <div
      className={clsx(
        CONTACT_GRID,
        'h-10 px-4 border-b border-[#2A2A2A]/50 text-[13px] transition-colors',
        isCurrentTarget
          ? 'bg-white/[0.06]'
          : 'bg-transparent hover:bg-white/[0.03]'
      )}
    >
      {/* Avatar */}
      <div className="flex justify-center">
        <div className="w-6 h-6 rounded-full bg-white/10 border border-white/10 overflow-hidden shrink-0 flex items-center justify-center">
          {contact.photoUrl ? (
            <img src={contact.photoUrl} alt={name} className="w-full h-full object-cover" />
          ) : (
            <span className="text-[9px] text-white/60 font-normal">
              {name.slice(0, 1).toUpperCase()}
            </span>
          )}
        </div>
      </div>
      {/* Name */}
      <div className="px-2 truncate text-white font-normal" title={name}>
        {name}
        {isCurrentTarget && (
          <span className="ml-2 text-[10px] font-normal text-white/60 bg-white/10 rounded px-1.5 py-0.5">
            {t.targetBadge}
          </span>
        )}
      </div>
      {/* Role */}
      <div className="px-2 truncate text-white/55" title={contact.title ?? ''}>
        {contact.title || contact.seniority || t.unknownRole}
      </div>
      {/* Email */}
      <div className="px-2 truncate text-white/55" title={contact.email ?? ''}>
        {contact.email || <span className="text-white/20 italic">{t.notAvailable}</span>}
      </div>
      {/* Phone */}
      <div className="px-2 truncate text-white/55" title={phone}>
        {phone || <span className="text-white/20 italic">{t.notAvailable}</span>}
      </div>
      {/* Action */}
      <div className="flex justify-center">
        {!isCurrentTarget && contact.email && (
          <button
            type="button"
            onClick={handleUseAsTarget}
            disabled={isSavingContact}
            className="h-6 rounded-md border border-white/15 bg-white/[0.06] px-2 text-[10px] font-normal text-white/60 transition-colors hover:bg-white/10 hover:text-white disabled:opacity-30"
          >
            {t.targetBadge}
          </button>
        )}
      </div>
    </div>
  );
});

interface DealContactTabProps {
  dealId: string;
  className?: string;
  highlightedActionTypes?: LeasingActionType[];
}

function DealContactTab({ dealId, className, highlightedActionTypes = [] }: DealContactTabProps) {
  const { kernel } = useKernel();
  const lang = useKernelStore(state => state.language);
  const t = translations[lang];

  const deal = useKernelStore(state => state.inventory.Deal.registry[dealId]) as DealInventoryEntityState | undefined;
  const linkedBrand = useKernelStore((state) =>
    deal?.brandId ? state.inventory.Brand.registry[deal.brandId] : null
  );
  const contactRegistry = useKernelStore((state) => state.inventory.Contact.registry);
  const patchContactRuntime = useCallback(
    (
      patch: Partial<
        Pick<
          DealRuntimeState,
          | 'selectedBrandContactId'
          | 'isDiscoveringContacts'
          | 'contactDiscoveryMessage'
          | 'contactDraftInitializedDealId'
          | 'contactNameDraft'
          | 'contactEmailDraft'
          | 'contactPhoneDraft'
          | 'contactRoleDraft'
        >
      >
    ) => {
      if (!kernel.current) return;
      kernel.current.patchEntityRuntimeState('Deal', dealId, patch);
    },
    [kernel, dealId]
  );
  // Recomputed on every render; `deal` subscribes to the merged Deal row, which changes reference
  // on every autosave write, so the indicator repaints exactly as before.
  const contactAutosaveState =
    kernel.current?.getAutosaveState('Deal', dealId, DEAL_CONTACT_AUTOSAVE_SCOPE) ?? DEFAULT_AUTOSAVE_STATE;
  const contactNameDraft = deal?.runtime?.contactNameDraft ?? deal?.contactName ?? '';
  const contactEmailDraft = deal?.runtime?.contactEmailDraft ?? deal?.contactEmail ?? '';
  const contactPhoneDraft = deal?.runtime?.contactPhoneDraft ?? deal?.contactPhone ?? '';
  const contactRoleDraft =
    deal?.runtime?.contactRoleDraft ?? extractRoleFromContactInfo(deal?.contactInfo ?? null);
  const selectedBrandContactId = deal?.runtime?.selectedBrandContactId ?? null;
  const isSavingContact =
    contactAutosaveState.saveState === 'queued' || contactAutosaveState.saveState === 'saving';
  const contactSaveState: DealContactSaveState =
    contactAutosaveState.saveState === 'error'
      ? 'error'
      : contactAutosaveState.saveState === 'saved'
        ? 'saved'
        : 'idle';
  const isDiscoveringContacts = deal?.isDiscoveringContacts === true;
  const contactDiscoveryMessage = deal?.contactDiscoveryMessage ?? null;
  const setContactNameDraft = useCallback((value: string) => {
    patchContactRuntime({ contactNameDraft: value });
  }, [patchContactRuntime]);
  const setContactEmailDraft = useCallback((value: string) => {
    patchContactRuntime({ contactEmailDraft: value });
  }, [patchContactRuntime]);
  const setContactPhoneDraft = useCallback((value: string) => {
    patchContactRuntime({ contactPhoneDraft: value });
  }, [patchContactRuntime]);
  const setContactRoleDraft = useCallback((value: string) => {
    patchContactRuntime({ contactRoleDraft: value });
  }, [patchContactRuntime]);
  const setSelectedBrandContactId = useCallback((value: string | null) => {
    patchContactRuntime({ selectedBrandContactId: value });
  }, [patchContactRuntime]);
  const setIsDiscoveringContacts = useCallback((value: boolean) => {
    patchContactRuntime({ isDiscoveringContacts: value });
  }, [patchContactRuntime]);
  const setContactDiscoveryMessage = useCallback((value: string | null) => {
    patchContactRuntime({ contactDiscoveryMessage: value });
  }, [patchContactRuntime]);

  useEffect(() => {
    if (kernel.current && dealId && !deal) {
      kernel.current.syncEntity('Deal', dealId);
    }
  }, [dealId, deal, kernel]);

  useEffect(() => {
    if (!kernel.current || !deal?.brandId) return;
    if (linkedBrand && Array.isArray(linkedBrand.contactIds)) return;
    kernel.current.syncEntity('Brand', deal.brandId);
  }, [kernel, deal?.brandId, linkedBrand]);

  const brandContactIds = useMemo(
    () => (Array.isArray(linkedBrand?.contactIds) ? linkedBrand.contactIds : []),
    [linkedBrand?.contactIds]
  );

  useEffect(() => {
    if (!kernel.current || brandContactIds.length === 0) return;
    void kernel.current.ensureInventoryIds('Contact', brandContactIds);
  }, [kernel, brandContactIds]);

  const isContactEmailValid = useMemo(
    () => contactEmailDraft.trim().length > 0 && isValidEmail(contactEmailDraft),
    [contactEmailDraft]
  );

  const hasContactChanges =
    contactAutosaveState.dirty ||
    contactAutosaveState.saveState === 'queued' ||
    contactAutosaveState.saveState === 'saving';

  const contactDraftPayload = useMemo<DealContactDraftPayload>(() => ({
    name: normalizeContactField(contactNameDraft),
    email: normalizeContactField(contactEmailDraft),
    phone: normalizeContactField(contactPhoneDraft),
    role: normalizeContactField(contactRoleDraft),
    company: normalizeContactField(linkedBrand?.name ?? ''),
  }), [contactNameDraft, contactEmailDraft, contactPhoneDraft, contactRoleDraft, linkedBrand?.name]);

  const applyContactDraft = useCallback((nextDraft: DealContactDraftPayload) => {
    patchContactRuntime({
      contactNameDraft: nextDraft.name ?? '',
      contactEmailDraft: nextDraft.email ?? '',
      contactPhoneDraft: nextDraft.phone ?? '',
      contactRoleDraft: nextDraft.role ?? '',
    });
  }, [patchContactRuntime]);

  const initializeContactDraft = useCallback((nextDraft: DealContactDraftPayload) => {
    patchContactRuntime({
      contactDraftInitializedDealId: dealId,
      contactNameDraft: nextDraft.name ?? '',
      contactEmailDraft: nextDraft.email ?? '',
      contactPhoneDraft: nextDraft.phone ?? '',
      contactRoleDraft: nextDraft.role ?? '',
    });
  }, [patchContactRuntime, dealId]);
  const isContactDraftInitialized = deal?.runtime?.contactDraftInitializedDealId === dealId;

  const contactAutosaveAdapter = useMemo<
    AutoSaveAdapter<LeasingDealDTO, DealContactDraftPayload, DealContactDraftPayload>
  >(() => ({
    mapToDraft: (server) => ({
      name: normalizeContactField(server.contactName ?? ''),
      email: normalizeContactField(server.contactEmail ?? ''),
      phone: normalizeContactField(server.contactPhone ?? ''),
      role: normalizeContactField(extractDealContactRole(server)),
      company: normalizeContactField(extractDealContactCompany(server, linkedBrand?.name ?? null)),
    }),
    mapToPayload: (nextDraft) => nextDraft,
    serialize: serializeContactPayload,
  }), [linkedBrand?.name]);

  useEntityDraftAutosave<LeasingDealDTO, DealContactDraftPayload, DealContactDraftPayload>({
    kernel,
    entityType: 'Deal',
    entityId: dealId,
    scope: DEAL_CONTACT_AUTOSAVE_SCOPE,
    serverData: deal ?? null,
    draft: contactDraftPayload,
    setDraft: applyContactDraft,
    isDraftInitialized: isContactDraftInitialized,
    initializeDraft: initializeContactDraft,
    adapter: contactAutosaveAdapter,
    reason: 'Autosave deal contact info',
    debounceMs: CONTACT_AUTOSAVE_DEBOUNCE_MS,
    validate: (nextDraft) =>
      typeof nextDraft.email === 'string' &&
      nextDraft.email.trim().length > 0 &&
      isValidEmail(nextDraft.email),
    persist: async (payload) => {
      if (!kernel.current) return false;
      await kernel.current.mutateInventory(
        [
          {
            action: 'UPDATE',
            entity: 'Deal',
            id: dealId,
            payload: {
              contactInfo: {
                name: payload.name,
                email: payload.email,
                phone: payload.phone,
                company: payload.company,
                role: payload.role,
                title: payload.role,
              },
            } as any,
          },
        ],
        'Autosave deal contact info'
      );
    },
    onSuccess: async () => {
      if (!kernel.current) return;
      await kernel.current.refreshEntityActionGroup('Deal', dealId);
    },
  });

  const contacts = useMemo<ContactDTO[]>(
    () => brandContactIds.map((id) => contactRegistry[id]).filter(Boolean) as ContactDTO[],
    [brandContactIds, contactRegistry]
  );

  const isContactsLoading = Boolean(deal?.brandId) && (
    !linkedBrand ||
    linkedBrand.contactIds === undefined ||
    brandContactIds.some((id) => !contactRegistry[id])
  );
  const lastDiscoveryAt = linkedBrand?.lastContactDiscoveryAt ? new Date(linkedBrand.lastContactDiscoveryAt).getTime() : 0;
  const isExhausted = linkedBrand?.contactDiscoveryStatus === 'EXHAUSTED';
  const cooldownMs = 60 * 24 * 60 * 60 * 1000;
  // eslint-disable-next-line react-hooks/purity -- pre-existing in this file, inherited by the merge gate (it judges whole files); changing it alters behaviour outside this merge
  const now = Date.now();
  const inCooldown = isExhausted && lastDiscoveryAt > 0 && now - lastDiscoveryAt < cooldownMs;
  const remainingDays = inCooldown
    ? Math.max(1, Math.ceil((cooldownMs - (now - lastDiscoveryAt)) / (1000 * 60 * 60 * 24)))
    : 0;

  const getContactDisplayName = useCallback((contact: ContactDTO): string => {
    if (contact.fullName?.trim()) return contact.fullName.trim();
    const fromParts = [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim();
    return fromParts || t.unknownName;
  }, [t.unknownName]);

  const selectedBrandContact = useMemo(
    () => contacts.find((contact) => contact.id === selectedBrandContactId) ?? null,
    [contacts, selectedBrandContactId]
  );

  useEffect(() => {
    if (!selectedBrandContactId) return;
    if (!contacts.some((contact) => contact.id === selectedBrandContactId)) {
      setSelectedBrandContactId(null);
    }
  }, [contacts, selectedBrandContactId, setSelectedBrandContactId]);

  const handleUseContactAsTarget = useCallback((contact: ContactDTO) => {
    const name = normalizeContactField(getContactDisplayName(contact));
    const email = normalizeContactField(contact.email ?? '');
    if (!email) {
      kernel.current?.handleError(t.invalidEmail);
      return;
    }
    const phone = normalizeContactField(contact.directPhone ?? contact.primaryPhone ?? '');
    const role = normalizeContactField(contact.title ?? contact.seniority ?? '');
    patchContactRuntime({
      contactNameDraft: name ?? '',
      contactEmailDraft: email ?? '',
      contactPhoneDraft: phone ?? '',
      contactRoleDraft: role ?? '',
    });
  }, [getContactDisplayName, kernel, patchContactRuntime, t.invalidEmail]);

  const handleDiscoverContacts = useCallback(async () => {
    if (!kernel.current || !deal?.brandId || isDiscoveringContacts) return;
    setIsDiscoveringContacts(true);
    setContactDiscoveryMessage(null);
    try {
      let domain: string | undefined;
      const website = normalizeContactField(linkedBrand?.websiteUrl ?? '');
      if (website) {
        try {
          domain = new URL(website.startsWith('http') ? website : `https://${website}`).hostname.replace(/^www\./i, '');
        } catch {
          domain = undefined;
        }
      }

      await kernel.current.discoverBrandContacts(deal.brandId, {
        maxResults: 12,
        enrich: true,
        revealPersonalEmails: true,
        revealPhoneNumber: true,
        seniorities: ['director', 'head', 'vp', 'c_suite', 'founder', 'owner', 'partner'],
        countries: ['France'],
        ...(domain ? { domain } : {}),
      });
      await kernel.current.syncEntity('Brand', deal.brandId);
      setContactDiscoveryMessage(t.contactDiscoveryLaunched);
    } catch {
      setContactDiscoveryMessage(t.contactDiscoveryFailed);
    } finally {
      setIsDiscoveringContacts(false);
    }
  }, [
    kernel,
    deal?.brandId,
    linkedBrand?.websiteUrl,
    isDiscoveringContacts,
    t.contactDiscoveryLaunched,
    t.contactDiscoveryFailed,
    setContactDiscoveryMessage,
    setIsDiscoveringContacts,
  ]);

  const handleContactNameChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setContactNameDraft(event.target.value);
  }, [setContactNameDraft]);
  const handleContactRoleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setContactRoleDraft(event.target.value);
  }, [setContactRoleDraft]);
  const handleContactEmailChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setContactEmailDraft(event.target.value);
  }, [setContactEmailDraft]);
  const handleContactPhoneChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setContactPhoneDraft(event.target.value);
  }, [setContactPhoneDraft]);
  const handleDiscoverContactsClick = useCallback(() => {
    void handleDiscoverContacts();
  }, [handleDiscoverContacts]);

  const isContactHighlighted = highlightedActionTypes.includes('SELECT_DEAL_CONTACT');
  const currentTargetEmail = normalizeContactField(deal?.contactEmail ?? '');

  if (!deal) {
    return (
      <div className="flex h-full items-center justify-center text-white/20 text-sm animate-pulse">
        {t.loadingContactWorkspace}
      </div>
    );
  }

  return (
    <div className={clsx("flex flex-col bg-black text-white font-sans", className)}>
      {/* Deal contact target: editable row */}
      <div className="relative border-b border-[#2A2A2A] bg-[#1D1D1D]">
        {isContactHighlighted && (
          <span className="absolute right-3 top-3 h-1.5 w-1.5 rounded-full bg-amber-300 shadow-[0_0_10px_rgba(252,211,77,0.9)]" />
        )}
        <div className="px-5 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="text-[11px] font-normal text-white/40 shrink-0">
              {t.useAsIncentiveTarget.replace('Use as ', '').replace('Utiliser comme ', '')}
            </div>
            <div className="flex items-center gap-2 min-w-0 flex-1 flex-wrap">
              <input
                type="text"
                className="h-7 rounded-md border border-transparent bg-transparent px-2 text-[13px] text-white placeholder-white/30 hover:border-white/10 focus:border-white/20 focus:bg-white/[0.03] focus:outline-none transition-colors min-w-[100px] flex-1"
                value={contactNameDraft}
                onChange={handleContactNameChange}
                placeholder={t.placeholderName}
              />
              <input
                type="text"
                className="h-7 rounded-md border border-transparent bg-transparent px-2 text-[13px] text-white/70 placeholder-white/30 hover:border-white/10 focus:border-white/20 focus:bg-white/[0.03] focus:outline-none transition-colors min-w-[100px] flex-1"
                value={contactRoleDraft}
                onChange={handleContactRoleChange}
                placeholder={t.placeholderRole}
              />
              <input
                type="email"
                className={clsx(
                  "h-7 rounded-md border border-transparent bg-transparent px-2 text-[13px] text-white/70 placeholder-white/30 hover:border-white/10 focus:border-white/20 focus:bg-white/[0.03] focus:outline-none transition-colors min-w-[140px] flex-1",
                  contactEmailDraft.trim().length > 0 && !isContactEmailValid && "!border-rose-500/45"
                )}
                value={contactEmailDraft}
                onChange={handleContactEmailChange}
                placeholder={t.placeholderEmail}
              />
              <input
                type="text"
                className="h-7 rounded-md border border-transparent bg-transparent px-2 text-[13px] text-white/70 placeholder-white/30 hover:border-white/10 focus:border-white/20 focus:bg-white/[0.03] focus:outline-none transition-colors min-w-[100px] w-[140px]"
                value={contactPhoneDraft}
                onChange={handleContactPhoneChange}
                placeholder={t.placeholderPhone}
              />
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div
              className={clsx(
                "text-[11px] font-normal",
                contactSaveState === 'saved' && "text-white/60",
                contactSaveState === 'error' && "text-rose-300/85",
                contactSaveState === 'idle' && "text-white/25"
              )}
            >
              {contactSaveState === 'saved'
                ? t.contactSaved
                : contactSaveState === 'error'
                  ? t.saveFailed
                  : hasContactChanges
                    ? t.unsavedChanges
                    : t.upToDate}
            </div>
            {isSavingContact && (
              <div className="text-[11px] font-normal text-white/40">{t.saving}</div>
            )}
          </div>
        </div>
        {contactEmailDraft.trim().length > 0 && !isContactEmailValid && (
          <div className="px-5 pb-2 -mt-1 text-[11px] text-rose-300">{t.invalidEmail}</div>
        )}
      </div>

      {/* Table */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Toolbar */}
        <div className="h-[44px] border-b border-[#2A2A2A] px-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="text-[13px] text-white/55 font-normal">{t.brandContacts}</span>
            <span className="text-[11px] text-white/30 bg-white/5 px-1.5 py-0.5 rounded">{contacts.length}</span>
          </div>
          {contacts.length === 0 && !isContactsLoading && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleDiscoverContactsClick}
                disabled={isDiscoveringContacts || inCooldown}
                className={clsx(
                  "h-7 rounded-md border px-3 text-[11px] font-normal transition-colors",
                  (isDiscoveringContacts || inCooldown)
                    ? "cursor-not-allowed border-white/10 bg-white/[0.03] text-white/25"
                    : "border-white/15 bg-white/[0.06] text-white/70 hover:bg-white/10 hover:text-white"
                )}
                title={inCooldown ? `Cooldown active for ${remainingDays} more days` : undefined}
              >
                {isDiscoveringContacts ? t.discovering : t.discoverContacts}
              </button>
              {inCooldown && (
                <span className="text-[10px] text-amber-500/70">
                  Cooldown: {remainingDays}d
                </span>
              )}
            </div>
          )}
          {contactDiscoveryMessage && (
            <span className="text-[11px] text-white/40">{contactDiscoveryMessage}</span>
          )}
        </div>

        {/* Header */}
        <div
          className={clsx(
            CONTACT_GRID,
            'h-9 bg-[#1D1D1D] border-b border-[#2A2A2A] px-4 text-[11px] font-normal text-white/45'
          )}
          style={CONTACT_HEADER_FONT_STYLE}
        >
          <div />
          <div className="px-2 flex items-center gap-1.5"><Type size={12} className="text-white/30 shrink-0" />{t.contactNameLabel}</div>
          <div className="px-2 flex items-center gap-1.5"><Briefcase size={12} className="text-white/30 shrink-0" />{t.contactRoleLabel}</div>
          <div className="px-2 flex items-center gap-1.5"><Mail size={12} className="text-white/30 shrink-0" />{t.contactEmailLabel}</div>
          <div className="px-2 flex items-center gap-1.5"><Phone size={12} className="text-white/30 shrink-0" />{t.contactPhoneLabel}</div>
          <div />
        </div>

        {/* Rows */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {isContactsLoading && (
            <div className="flex flex-col gap-0">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className={clsx(CONTACT_GRID, 'h-10 px-4 border-b border-[#2A2A2A]/50 animate-pulse')}>
                  <div className="flex justify-center"><div className="h-6 w-6 rounded-full bg-white/5" /></div>
                  <div className="px-2"><div className="h-3 w-[70%] rounded bg-white/5" /></div>
                  <div className="px-2"><div className="h-3 w-[50%] rounded bg-white/[0.03]" /></div>
                  <div className="px-2"><div className="h-3 w-[60%] rounded bg-white/[0.03]" /></div>
                  <div className="px-2"><div className="h-3 w-[45%] rounded bg-white/[0.03]" /></div>
                  <div />
                </div>
              ))}
            </div>
          )}

          {!isContactsLoading && contacts.map((contact) => (
            <DealContactRow
              key={contact.id}
              contact={contact}
              name={getContactDisplayName(contact)}
              currentTargetEmail={currentTargetEmail}
              isSavingContact={isSavingContact}
              t={t}
              onUseAsTarget={handleUseContactAsTarget}
            />
          ))}

          {!isContactsLoading && contacts.length === 0 && (
            <div className="p-8 text-center space-y-3">
              <div className="text-[12px] text-white/25 italic">{t.noContactsOnBrand}</div>
              <div className="text-[11px] text-white/35">{t.runDiscoveryDesc}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const buildDraftPayloadFromDeal = (value: LeasingDealDTO): DealEconomicsDraftPayload => {
  const hasStepRents = Boolean(value.incentiveHasStepRents);
  const incentiveSteps = hasStepRents && Array.isArray(value.incentiveSteps)
    ? value.incentiveSteps.map((step, index) => ({
        stepOrder: typeof step.stepOrder === 'number' && Number.isFinite(step.stepOrder)
          ? step.stepOrder
          : index + 1,
        startMonth: step.startMonth ?? 1,
        endMonth: step.endMonth ?? 12,
        rentPercent: step.rentPercent,
      }))
    : [];

  return {
    rentAmount: Number(value.rentAmount) || 0,
    leaseFirmPeriodYears: Number(value.leaseFirmPeriodYears) || 0,
    leaseDurationYears: Number(value.leaseDurationYears) || 0,
    incentiveRentFreeMonths: value.incentiveRentFreeMonths || 0,
    incentiveFitOutAmount: value.incentiveFitOutAmount ? Number(value.incentiveFitOutAmount) : 0,
    estimatedTenantFitoutCost:
      value.estimatedTenantFitoutCost === null || value.estimatedTenantFitoutCost === undefined
        ? null
        : Number(value.estimatedTenantFitoutCost),
    estimatedTenantFitoutCostPerSqm:
      value.estimatedTenantFitoutCostPerSqm === null || value.estimatedTenantFitoutCostPerSqm === undefined
        ? null
        : Number(value.estimatedTenantFitoutCostPerSqm),
    incentiveHasStepRents: hasStepRents,
    incentiveSteps,
  };
};

const NUM_INPUT = clsx(
  "w-full h-8 bg-transparent border border-transparent rounded-md text-[13px] font-mono text-white px-2 outline-none hover:border-white/10 focus:border-white/20 focus:bg-white/[0.03] transition-colors",
  "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
);
const NUM_INPUT_W80 = clsx(NUM_INPUT, "max-w-[80px]");
const NUM_INPUT_W50 = clsx(NUM_INPUT, "max-w-[50px]");
const NUM_INPUT_W40 = clsx(NUM_INPUT, "max-w-[40px]");
const ECON_GRID = 'grid grid-cols-[1fr_0.8fr_0.5fr_1.2fr] gap-0 items-center';
const ROW_CLS = 'h-10 px-4 border-b border-[#2A2A2A]/50 text-[13px] transition-colors hover:bg-white/[0.03]';
const LABEL_CLS = 'px-2 text-white/45 font-normal truncate';
const VALUE_CLS = 'px-2 text-white font-normal';
const UNIT_CLS = 'px-2 text-white/25 text-[11px] font-normal truncate';
const DESC_CLS = 'px-2 text-white/20 text-[11px] font-normal truncate';

interface DealStepRowProps {
  index: number;
  startMonth: number;
  endMonth: number;
  rentPercent: number;
  lang: 'en' | 'fr';
  onPatchStep: (index: number, patch: Partial<DealEconomicsStepDraft>) => void;
  onRemoveStep: (index: number) => void;
}

const DealStepRow = React.memo(function DealStepRow({
  index,
  startMonth,
  endMonth,
  rentPercent,
  lang,
  onPatchStep,
  onRemoveStep,
}: DealStepRowProps) {
  const handleStartMonthChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onPatchStep(index, { startMonth: Number(event.target.value) });
  }, [onPatchStep, index]);
  const handleEndMonthChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onPatchStep(index, { endMonth: Number(event.target.value) });
  }, [onPatchStep, index]);
  const handleRentPercentChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onPatchStep(index, { rentPercent: Number(event.target.value) });
  }, [onPatchStep, index]);
  const handleRemove = useCallback(() => {
    onRemoveStep(index);
  }, [onRemoveStep, index]);

  return (
    <div className={clsx(ECON_GRID, ROW_CLS)}>
      <div className={clsx(LABEL_CLS, 'pl-6')}>{lang === 'fr' ? 'Palier' : 'Step'} {index + 1}</div>
      <div className={VALUE_CLS}>
        <div className="flex items-center gap-1">
          <input type="number" className={NUM_INPUT_W40} value={startMonth} onChange={handleStartMonthChange} />
          <span className="text-white/15">–</span>
          <input type="number" className={NUM_INPUT_W40} value={endMonth} onChange={handleEndMonthChange} />
        </div>
      </div>
      <div className={UNIT_CLS}>
        <div className="flex items-center gap-1">
          <input type="number" className={NUM_INPUT_W50} value={rentPercent} onChange={handleRentPercentChange} />
          <span className="text-white/20 text-xs">%</span>
        </div>
      </div>
      <div className="px-2 flex items-center justify-between">
        <span className={DESC_CLS}>{lang === 'fr' ? `Mois ${startMonth}→${endMonth}` : `Month ${startMonth}→${endMonth}`}</span>
        <button onClick={handleRemove} className="text-white/10 hover:text-rose-500 transition-colors p-1 rounded hover:bg-rose-500/10 shrink-0"><Trash2 size={13}/></button>
      </div>
    </div>
  );
});

interface DealEconomicsTabPanelProps {
  dealId: string;
  initialTab?: DealEconomicsTab;
  className?: string;
  highlightedActionTypes?: LeasingActionType[];
}

function DealEconomicsTabPanel({ dealId, initialTab = 'RENT_FREE', className, highlightedActionTypes = [] }: DealEconomicsTabPanelProps) {
  const { kernel } = useKernel();
  const lang = useKernelStore(state => state.language);
  const t = translations[lang];

  const deal = useKernelStore(state => state.inventory.Deal.registry[dealId]) as DealInventoryEntityState | undefined;
  const linkedUnit = useKernelStore((state) =>
    deal?.unitId ? state.inventory.Unit.registry[deal.unitId] : null
  );
  const linkedBrand = useKernelStore((state) =>
    deal?.brandId ? state.inventory.Brand.registry[deal.brandId] : null
  );
  const primaryLegalEntityId = linkedBrand?.primaryLegalEntityId;
  const primaryLegalEntity = useKernelStore(state => primaryLegalEntityId ? state.inventory.LegalEntity.registry[primaryLegalEntityId] : null);

  const patchEconomicsState = useCallback((patch: DealEconomicsRuntimeDraftPatch) => {
    if (!kernel.current) return;
    kernel.current.patchEntityRuntimeState('Deal', dealId, patch);
  }, [kernel, dealId]);

  const economicsRuntime = deal?.runtime;

  // Step 1: Foundation State
  const baseRent =
    typeof economicsRuntime?.economicsRentAmountDraft === 'number' && Number.isFinite(economicsRuntime.economicsRentAmountDraft)
      ? economicsRuntime.economicsRentAmountDraft
      : typeof deal?.rentAmount === 'number' && Number.isFinite(deal.rentAmount)
        ? deal.rentAmount
        : 0;
  const firmYears =
    typeof economicsRuntime?.economicsLeaseFirmPeriodYearsDraft === 'number' && Number.isFinite(economicsRuntime.economicsLeaseFirmPeriodYearsDraft)
      ? economicsRuntime.economicsLeaseFirmPeriodYearsDraft
      : typeof deal?.leaseFirmPeriodYears === 'number' && Number.isFinite(deal.leaseFirmPeriodYears)
      ? deal.leaseFirmPeriodYears
      : 0;
  const leaseDurationYears =
    typeof economicsRuntime?.economicsLeaseDurationYearsDraft === 'number' && Number.isFinite(economicsRuntime.economicsLeaseDurationYearsDraft)
      ? economicsRuntime.economicsLeaseDurationYearsDraft
      : typeof deal?.leaseDurationYears === 'number' && Number.isFinite(deal.leaseDurationYears)
      ? deal.leaseDurationYears
      : 0;

  // Step 2: Simulation State
  const rentFreeMonths =
    typeof economicsRuntime?.economicsIncentiveRentFreeMonthsDraft === 'number' && Number.isFinite(economicsRuntime.economicsIncentiveRentFreeMonthsDraft)
      ? economicsRuntime.economicsIncentiveRentFreeMonthsDraft
      : typeof deal?.incentiveRentFreeMonths === 'number' && Number.isFinite(deal.incentiveRentFreeMonths)
      ? deal.incentiveRentFreeMonths
      : 0;
  const fitOutAmount =
    typeof economicsRuntime?.economicsIncentiveFitOutAmountDraft === 'number' && Number.isFinite(economicsRuntime.economicsIncentiveFitOutAmountDraft)
      ? economicsRuntime.economicsIncentiveFitOutAmountDraft
      : typeof deal?.incentiveFitOutAmount === 'number' && Number.isFinite(deal.incentiveFitOutAmount)
      ? deal.incentiveFitOutAmount
      : 0;
  const estimatedTenantFitoutCostDraft =
    typeof economicsRuntime?.economicsEstimatedTenantFitoutCostDraft === 'number' && Number.isFinite(economicsRuntime.economicsEstimatedTenantFitoutCostDraft)
      ? economicsRuntime.economicsEstimatedTenantFitoutCostDraft
      : typeof deal?.estimatedTenantFitoutCost === 'number' && Number.isFinite(deal.estimatedTenantFitoutCost)
      ? deal.estimatedTenantFitoutCost
      : null;
  const hasStepRents =
    typeof economicsRuntime?.economicsIncentiveHasStepRentsDraft === 'boolean'
      ? economicsRuntime.economicsIncentiveHasStepRentsDraft
      : deal?.incentiveHasStepRents === true;
  const steps = useMemo<DealEconomicsStepDraft[]>(
    () =>
      Array.isArray(economicsRuntime?.economicsIncentiveStepsDraft)
        ? economicsRuntime.economicsIncentiveStepsDraft.reduce<DealEconomicsStepDraft[]>((acc, step) => {
            if (!step) return acc;
            const startMonth = typeof step.startMonth === 'number' ? step.startMonth : null;
            const endMonth = typeof step.endMonth === 'number' ? step.endMonth : null;
            const rentPercent = typeof step.rentPercent === 'number' ? step.rentPercent : null;
            if (
              startMonth === null ||
              !Number.isFinite(startMonth) ||
              endMonth === null ||
              !Number.isFinite(endMonth) ||
              rentPercent === null ||
              !Number.isFinite(rentPercent)
            ) {
              return acc;
            }
            acc.push({ startMonth, endMonth, rentPercent });
            return acc;
          }, [])
        : Array.isArray(deal?.incentiveSteps)
          ? deal.incentiveSteps.reduce<DealEconomicsStepDraft[]>((acc, step) => {
            if (!step) return acc;
            const startMonth = typeof step.startMonth === 'number' ? step.startMonth : null;
            const endMonth = typeof step.endMonth === 'number' ? step.endMonth : null;
            const rentPercent = typeof step.rentPercent === 'number' ? step.rentPercent : null;
            if (
              startMonth === null ||
              !Number.isFinite(startMonth) ||
              endMonth === null ||
              !Number.isFinite(endMonth) ||
              rentPercent === null ||
              !Number.isFinite(rentPercent)
            ) {
              return acc;
            }
            acc.push({ startMonth, endMonth, rentPercent });
            return acc;
          }, [])
          : [],
    [economicsRuntime?.economicsIncentiveStepsDraft, deal?.incentiveSteps]
  );

  const setBaseRent = useCallback((value: number) => {
    patchEconomicsState({ economicsRentAmountDraft: value });
  }, [patchEconomicsState]);
  const setFirmYears = useCallback((value: number) => {
    patchEconomicsState({ economicsLeaseFirmPeriodYearsDraft: value });
  }, [patchEconomicsState]);
  const setLeaseDurationYears = useCallback((value: number) => {
    patchEconomicsState({ economicsLeaseDurationYearsDraft: value });
  }, [patchEconomicsState]);
  const setRentFreeMonths = useCallback((value: number) => {
    patchEconomicsState({ economicsIncentiveRentFreeMonthsDraft: value });
  }, [patchEconomicsState]);
  const setFitOutAmount = useCallback((value: number) => {
    patchEconomicsState({ economicsIncentiveFitOutAmountDraft: value });
  }, [patchEconomicsState]);
  const setEstimatedTenantFitoutCostDraft = useCallback((value: number | null) => {
    patchEconomicsState({ economicsEstimatedTenantFitoutCostDraft: value });
  }, [patchEconomicsState]);
  const setHasStepRents = useCallback((value: boolean) => {
    patchEconomicsState({ economicsIncentiveHasStepRentsDraft: value });
  }, [patchEconomicsState]);
  const setSteps = useCallback(
    (
      value:
        | DealEconomicsStepDraft[]
        | ((prev: DealEconomicsStepDraft[]) => DealEconomicsStepDraft[])
    ) => {
      const next = typeof value === 'function' ? value(steps) : value;
      patchEconomicsState({
        economicsIncentiveStepsDraft: next.map((step, index) => ({
          stepOrder: index + 1,
          startMonth: step.startMonth,
          endMonth: step.endMonth,
          rentPercent: step.rentPercent,
        })),
      });
    },
    [steps, patchEconomicsState]
  );

  const handleBaseRentChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setBaseRent(Math.max(0, Number(event.target.value) || 0));
  }, [setBaseRent]);
  const handleFirmYearsChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setFirmYears(Number(event.target.value));
  }, [setFirmYears]);
  const handleLeaseDurationYearsChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setLeaseDurationYears(Number(event.target.value));
  }, [setLeaseDurationYears]);
  const handleRentFreeMonthsChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRentFreeMonths(Math.max(0, Math.round(Number(event.target.value) || 0)));
  }, [setRentFreeMonths]);
  const handleEstimatedTenantFitoutCostChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value.trim();
    if (!raw) { setEstimatedTenantFitoutCostDraft(null); return; }
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return;
    setEstimatedTenantFitoutCostDraft(Math.max(0, parsed));
  }, [setEstimatedTenantFitoutCostDraft]);
  const handleFitOutAmountChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setFitOutAmount(Math.max(0, Number(event.target.value) || 0));
  }, [setFitOutAmount]);
  const handlePatchStep = useCallback((index: number, patch: Partial<DealEconomicsStepDraft>) => {
    setSteps((prev) => prev.map((step, i) => (i === index ? { ...step, ...patch } : step)));
  }, [setSteps]);
  const handleRemoveStep = useCallback((index: number) => {
    setSteps((prev) => prev.filter((_, i) => i !== index));
  }, [setSteps]);
  const handleAddStep = useCallback(() => {
    setSteps((prev) => [
      ...prev,
      {
        startMonth: (prev[prev.length - 1]?.endMonth || 0) + 1,
        endMonth: (prev[prev.length - 1]?.endMonth || 0) + 12,
        rentPercent: 100,
      },
    ]);
  }, [setSteps]);

  const incentiveSectionRefs = useRef<Record<'RENT_FREE' | 'FITOUT' | 'STEPS', HTMLElement | null>>({
    RENT_FREE: null,
    FITOUT: null,
    STEPS: null,
  });

  useEffect(() => {
    if (!kernel.current || !dealId) return;

    if (!deal) {
      kernel.current.syncEntity('Deal', dealId);
      return;
    }

    if (deal.unitId && !linkedUnit) {
      kernel.current.syncEntity('Unit', deal.unitId);
    }

    if (deal.brandId && !linkedBrand) {
      kernel.current.syncEntity('Brand', deal.brandId);
    }
  }, [dealId, deal, linkedUnit, linkedBrand, kernel]);

  useEffect(() => {
    const nextTab = initialTab === 'CONTACT' ? 'RENT_FREE' : initialTab;
    const anchorTab = nextTab;
    const target = incentiveSectionRefs.current[anchorTab];
    if (!target) return;
    const raf = window.requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => {
      window.cancelAnimationFrame(raf);
    };
  }, [dealId, initialTab]);

  const draftPayload = useMemo<DealEconomicsDraftPayload>(() => {
    const areaSqmDirect = parsePositiveNumber(linkedUnit?.areaSqm);
    const areaSqftFallback = parsePositiveNumber(linkedUnit?.areaSqft);
    const areaSqm = areaSqmDirect ?? (areaSqftFallback ? areaSqftFallback * SQFT_TO_SQM : null);
    const estimatedTenantFitoutCostPerSqm =
      estimatedTenantFitoutCostDraft !== null && areaSqm && areaSqm > 0
        ? estimatedTenantFitoutCostDraft / areaSqm
        : null;

    return {
      rentAmount: baseRent,
      leaseFirmPeriodYears: firmYears,
      leaseDurationYears,
      incentiveRentFreeMonths: Math.max(0, Math.round(rentFreeMonths)),
      incentiveFitOutAmount: fitOutAmount,
      estimatedTenantFitoutCost: estimatedTenantFitoutCostDraft,
      estimatedTenantFitoutCostPerSqm,
      incentiveHasStepRents: hasStepRents,
      incentiveSteps: hasStepRents
        ? steps.map((s, i) => ({
            stepOrder: i + 1,
            startMonth: s.startMonth,
            endMonth: s.endMonth,
            rentPercent: s.rentPercent
          }))
        : []
    };
  }, [baseRent, firmYears, leaseDurationYears, rentFreeMonths, fitOutAmount, estimatedTenantFitoutCostDraft, hasStepRents, steps, linkedUnit]);

  const applyEconomicsDraft = useCallback((nextDraft: DealEconomicsDraftPayload) => {
    patchEconomicsState({
      economicsRentAmountDraft: nextDraft.rentAmount,
      economicsLeaseFirmPeriodYearsDraft: nextDraft.leaseFirmPeriodYears,
      economicsLeaseDurationYearsDraft: nextDraft.leaseDurationYears,
      economicsIncentiveRentFreeMonthsDraft: nextDraft.incentiveRentFreeMonths,
      economicsIncentiveFitOutAmountDraft: nextDraft.incentiveFitOutAmount,
      economicsEstimatedTenantFitoutCostDraft: nextDraft.estimatedTenantFitoutCost,
      economicsIncentiveHasStepRentsDraft: nextDraft.incentiveHasStepRents,
      economicsIncentiveStepsDraft: nextDraft.incentiveSteps.map((step, index) => ({
        stepOrder: typeof step.stepOrder === 'number' ? step.stepOrder : index + 1,
        startMonth: step.startMonth,
        endMonth: step.endMonth,
        rentPercent: step.rentPercent,
      })),
    });
  }, [patchEconomicsState]);

  const initializeEconomicsDraft = useCallback((nextDraft: DealEconomicsDraftPayload) => {
    patchEconomicsState({
      economicsDraftInitializedDealId: dealId,
      economicsRentAmountDraft: nextDraft.rentAmount,
      economicsLeaseFirmPeriodYearsDraft: nextDraft.leaseFirmPeriodYears,
      economicsLeaseDurationYearsDraft: nextDraft.leaseDurationYears,
      economicsIncentiveRentFreeMonthsDraft: nextDraft.incentiveRentFreeMonths,
      economicsIncentiveFitOutAmountDraft: nextDraft.incentiveFitOutAmount,
      economicsEstimatedTenantFitoutCostDraft: nextDraft.estimatedTenantFitoutCost,
      economicsIncentiveHasStepRentsDraft: nextDraft.incentiveHasStepRents,
      economicsIncentiveStepsDraft: nextDraft.incentiveSteps.map((step, index) => ({
        stepOrder: typeof step.stepOrder === 'number' ? step.stepOrder : index + 1,
        startMonth: step.startMonth,
        endMonth: step.endMonth,
        rentPercent: step.rentPercent,
      })),
    });
  }, [patchEconomicsState, dealId]);
  const isEconomicsDraftInitialized = deal?.runtime?.economicsDraftInitializedDealId === dealId;

  const economicsAutosaveAdapter = useMemo<
    AutoSaveAdapter<LeasingDealDTO, DealEconomicsDraftPayload, DealEconomicsDraftPayload>
  >(() => ({
    mapToDraft: (server) => buildDraftPayloadFromDeal(server),
    mapToPayload: (nextDraft) => nextDraft,
    serialize: serializeEconomicsPayload,
  }), []);

  useEntityDraftAutosave<LeasingDealDTO, DealEconomicsDraftPayload, DealEconomicsDraftPayload>({
    kernel,
    entityType: 'Deal',
    entityId: dealId,
    scope: DEAL_ECONOMICS_AUTOSAVE_SCOPE,
    serverData: deal ?? null,
    draft: draftPayload,
    setDraft: applyEconomicsDraft,
    isDraftInitialized: isEconomicsDraftInitialized,
    initializeDraft: initializeEconomicsDraft,
    adapter: economicsAutosaveAdapter,
    reason: 'Autosave deal economics',
    debounceMs: ECONOMICS_AUTOSAVE_DEBOUNCE_MS,
    persist: async (payload) => {
      if (!kernel.current) return false;
      await kernel.current.mutateInventory(
        [{
          action: 'UPDATE',
          entity: 'Deal',
          id: dealId,
          payload: payload as any,
        }],
        'Autosave deal economics'
      );
    },
    onSuccess: async () => {
      if (!kernel.current) return;
      await kernel.current.refreshEntityActionGroup('Deal', dealId);
    },
  });

  const stats = useMemo(() => {
    const areaSqmDirect = parsePositiveNumber(linkedUnit?.areaSqm);
    const areaSqftFallback = parsePositiveNumber(linkedUnit?.areaSqft);
    const areaSqm = areaSqmDirect ?? (areaSqftFallback ? areaSqftFallback * SQFT_TO_SQM : null);
    const monthlyRent = baseRent / 12;
    const firmMonths = Math.max(1, firmYears * 12);
    const effectiveLeaseDurationYears =
      leaseDurationYears > 0
        ? (firmYears > 0 ? Math.min(leaseDurationYears, firmYears) : leaseDurationYears)
        : (firmYears > 0 ? firmYears : 0);
    const totalContractValue = baseRent * effectiveLeaseDurationYears;
    const estimatedTenantFitout = estimatedTenantFitoutCostDraft ?? 0;
    
    // Risk Assessment
    const tenantHealth = primaryLegalEntity?.financialStatus || 'UNKNOWN';
    const isRiskyTenant = ['WATCHLIST', 'DISTRESSED'].includes(tenantHealth);

    // Concessions Math
    const { rentFreeValue: rfValue, totalIncentiveValue } = computeDealIncentiveEnvelope({
      annualRent: baseRent,
      rentFreeMonths,
      fitOutAmount,
      steps: hasStepRents ? steps : null,
    });
    const nerDiscountPercent = totalContractValue > 0 ? (totalIncentiveValue / totalContractValue) * 100 : 0;
    const netEffectiveAnnual = firmYears > 0 ? (totalContractValue - totalIncentiveValue) / firmYears : 0;

    // Advanced Metrics
    const assetValuation = baseRent / DEFAULT_CAP_RATE;
    const paybackMonths = monthlyRent > 0 ? totalIncentiveValue / monthlyRent : 0;
    const isPaybackDangerous = paybackMonths > firmMonths;
    const isFoundationSet = baseRent > 0 && firmYears > 0;

    return {
      areaSqm, monthlyRent, firmMonths, totalContractValue,
      estimatedTenantFitout, tenantHealth, isRiskyTenant,
      rfValue, totalIncentiveValue, nerDiscountPercent, netEffectiveAnnual,
      assetValuation, paybackMonths, isPaybackDangerous, isFoundationSet,
      fitOutCoveragePercent: estimatedTenantFitout > 0 ? (fitOutAmount / estimatedTenantFitout) * 100 : 0,
    };
  }, [linkedUnit, primaryLegalEntity, baseRent, firmYears, leaseDurationYears, rentFreeMonths, fitOutAmount, estimatedTenantFitoutCostDraft, hasStepRents, steps]);

  const cliffWarnings = useMemo(() => {
    const warnings: string[] = [];
    if (!hasStepRents) return warnings;
    for (let i = 1; i < steps.length; i++) {
      const prevStep = steps[i - 1];
      const currStep = steps[i];
      if (!prevStep || !currStep) continue;

      const prev = prevStep.rentPercent;
      const curr = currStep.rentPercent;
      if (prev > 0 && ((curr - prev) / prev) > 0.5) {
        warnings.push(t.jumpWarning(prev, curr, Math.round(((curr - prev) / prev) * 100)));
      }
    }
    return warnings;
  }, [hasStepRents, steps, t]);

  const pendingDealActionTypes = useMemo(() => {
    const types = new Set<string>();
    for (const actionType of highlightedActionTypes) {
      types.add(actionType);
    }
    return types;
  }, [highlightedActionTypes]);

  const isBaseRentHighlighted = pendingDealActionTypes.has('DECIDE_BASE_RENT');
  const isDurationHighlighted = pendingDealActionTypes.has('DECIDE_LEASE_DURATION');
  const isFoundationHighlighted = isBaseRentHighlighted || isDurationHighlighted;
  const isRentFreeHighlighted =
    pendingDealActionTypes.has('DECIDE_RENT_FREE_PERIOD') ||
    pendingDealActionTypes.has('VALIDATE_INCENTIVE_COVENANT');
  const isFitOutHighlighted =
    pendingDealActionTypes.has('DECIDE_FITOUT_CONTRIBUTION') ||
    pendingDealActionTypes.has('ESTIMATE_TENANT_FITOUT_COST');
  const isFitOutEstimatePending = pendingDealActionTypes.has('ESTIMATE_TENANT_FITOUT_COST');
  const isFitOutSuspicious =
    stats.estimatedTenantFitout > 0 &&
    fitOutAmount > 0 &&
    fitOutAmount > stats.estimatedTenantFitout * 2;
  const fitOutSuspiciousRatio =
    stats.estimatedTenantFitout > 0 ? (fitOutAmount / stats.estimatedTenantFitout).toFixed(1) : null;
  const fitOutSuspiciousText =
    lang === 'fr'
      ? `Suspicion: la subvention dépasse 200% de l'estimation (${fitOutSuspiciousRatio ?? '?'}x).`
      : `Suspicious: subsidy exceeds 200% of estimate (${fitOutSuspiciousRatio ?? '?'}x).`;
  const isStepsHighlighted = pendingDealActionTypes.has('DECIDE_STEP_RENTS');

  if (!deal) {
    return <div className="flex h-full items-center justify-center text-white/20 text-sm animate-pulse">{t.initializingFinancialEngine}</div>;
  }

  return (
    <div className={clsx("flex flex-col h-full bg-black text-white font-sans", className)}>
      {!stats.isFoundationSet && (
        <div className="px-4 py-2 border-b border-rose-500/20 bg-rose-500/10 text-[12px] text-rose-200/85">
          {t.foundationRequired}: {t.foundationRequiredDesc}
        </div>
      )}

      {/* Table */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Column header */}
        <div
          className={clsx(ECON_GRID, 'h-9 bg-[#1D1D1D] border-b border-[#2A2A2A] px-4 text-[11px] font-normal text-white/45')}
        >
          <div className="px-2 flex items-center gap-1.5"><Type size={12} className="text-white/30 shrink-0" />{lang === 'fr' ? 'Paramètre' : 'Parameter'}</div>
          <div className="px-2 flex items-center gap-1.5"><Hash size={12} className="text-white/30 shrink-0" />{lang === 'fr' ? 'Valeur' : 'Value'}</div>
          <div className="px-2 flex items-center gap-1.5"><AtSign size={12} className="text-white/30 shrink-0" />{lang === 'fr' ? 'Unité' : 'Unit'}</div>
          <div className="px-2 flex items-center gap-1.5"><FileText size={12} className="text-white/30 shrink-0" />{lang === 'fr' ? 'Description' : 'Description'}</div>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {/* Base Rent */}
          <div className={clsx(ECON_GRID, ROW_CLS, isBaseRentHighlighted && 'bg-white/[0.04]')}>
            <div className={LABEL_CLS}>{t.facialBaseRent}</div>
            <div className={VALUE_CLS}>
              <div className="flex items-center gap-1">
                <span className="text-white/25 text-xs">€</span>
                <input type="number" className={NUM_INPUT} value={baseRent || ''} onChange={handleBaseRentChange} placeholder="0" />
              </div>
            </div>
            <div className={UNIT_CLS}>€/{lang === 'fr' ? 'an' : 'yr'}</div>
            <div className={DESC_CLS}>
              {stats.areaSqm && baseRent ? `${Math.round(baseRent / stats.areaSqm)} ${t.perSqm}` : lang === 'fr' ? 'Loyer facial annuel' : 'Annual headline rent'}
            </div>
          </div>

          {linkedUnit?.askingRent && (
            <div className={clsx(ECON_GRID, ROW_CLS)}>
              <div className={LABEL_CLS}>{lang === 'fr' ? 'Loyer cible' : 'Target rent'}</div>
              <div className={clsx(VALUE_CLS, 'text-white/30')}>€{linkedUnit.askingRent.toLocaleString()}</div>
              <div className={UNIT_CLS}>€/{lang === 'fr' ? 'an' : 'yr'}</div>
              <div className={DESC_CLS}>{lang === 'fr' ? 'Loyer demandé pour l\'unité' : 'Asking rent for the unit'}</div>
            </div>
          )}

          {/* Firm period */}
          <div className={clsx(ECON_GRID, ROW_CLS, isDurationHighlighted && 'bg-white/[0.04]')}>
            <div className={LABEL_CLS}>{lang === 'fr' ? 'Période ferme' : 'Firm period'}</div>
            <div className={VALUE_CLS}>
              <input type="number" className={NUM_INPUT_W80} value={firmYears || ''} onChange={handleFirmYearsChange} placeholder="0" />
            </div>
            <div className={UNIT_CLS}>{lang === 'fr' ? 'ans' : 'yrs'}</div>
            <div className={DESC_CLS}>{lang === 'fr' ? 'Durée d\'engagement ferme du locataire' : 'Binding commitment period'}</div>
          </div>

          {/* Total duration */}
          <div className={clsx(ECON_GRID, ROW_CLS, isDurationHighlighted && 'bg-white/[0.04]')}>
            <div className={LABEL_CLS}>{lang === 'fr' ? 'Durée totale' : 'Total duration'}</div>
            <div className={VALUE_CLS}>
              <input type="number" className={NUM_INPUT_W80} value={leaseDurationYears || ''} onChange={handleLeaseDurationYearsChange} placeholder="0" />
            </div>
            <div className={UNIT_CLS}>{lang === 'fr' ? 'ans' : 'yrs'}</div>
            <div className={DESC_CLS}>{t.firmTotalYears(firmYears || 0, leaseDurationYears || 0)}</div>
          </div>

          {/* Rent Free */}
          <div className={clsx(ECON_GRID, ROW_CLS, isRentFreeHighlighted && 'bg-white/[0.04]')}>
            <div className={LABEL_CLS}>{t.rentFreeConcession}</div>
            <div className={VALUE_CLS}>
              <input type="number" className={NUM_INPUT_W80} value={rentFreeMonths || ''} onChange={handleRentFreeMonthsChange} placeholder="0" />
            </div>
            <div className={UNIT_CLS}>{lang === 'fr' ? 'mois' : 'mo'}</div>
            <div className={DESC_CLS}>€{stats.rfValue.toLocaleString(undefined, { maximumFractionDigits: 0 })} {lang === 'fr' ? 'de franchise' : 'concession value'}</div>
          </div>

          {/* Fit Out Estimate */}
          <div className={clsx(ECON_GRID, ROW_CLS, isFitOutHighlighted && 'bg-white/[0.04]')}>
            <div className={LABEL_CLS}>{t.tenantFitOutEstimate}</div>
            <div className={VALUE_CLS}>
              <div className="flex items-center gap-1">
                <span className="text-white/25 text-xs">€</span>
                <input
                  type="number"
                  className={NUM_INPUT}
                  value={estimatedTenantFitoutCostDraft ?? ''}
                  onChange={handleEstimatedTenantFitoutCostChange}
                  placeholder={'\u2014'}
                />
              </div>
            </div>
            <div className={UNIT_CLS}>€</div>
            <div className={DESC_CLS}>
              {stats.areaSqm && estimatedTenantFitoutCostDraft !== null
                ? `${Math.round(estimatedTenantFitoutCostDraft / stats.areaSqm)} ${t.perSqm}`
                : lang === 'fr' ? 'Coût estimé des travaux locataire' : 'Estimated tenant fit-out cost'}
            </div>
          </div>

          {/* Fit Out Contribution */}
          <div className={clsx(ECON_GRID, ROW_CLS, isFitOutHighlighted && 'bg-white/[0.04]')}>
            <div className={LABEL_CLS}>{t.fitOutContribution}</div>
            <div className={VALUE_CLS}>
              <div className="flex items-center gap-1">
                <span className="text-white/25 text-xs">€</span>
                <input type="number" className={NUM_INPUT} value={fitOutAmount || ''} onChange={handleFitOutAmountChange} placeholder="0" />
              </div>
            </div>
            <div className={UNIT_CLS}>€</div>
            <div className={DESC_CLS}>
              {stats.fitOutCoveragePercent > 0
                ? `${stats.fitOutCoveragePercent.toFixed(0)}% ${lang === 'fr' ? 'couverture' : 'coverage'}${stats.areaSqm && fitOutAmount ? ` · ${Math.round(fitOutAmount / stats.areaSqm)} ${t.perSqm}` : ''}`
                : lang === 'fr' ? 'Participation bailleur aux travaux' : 'Landlord contribution to fit-out'}
            </div>
          </div>

          {isFitOutSuspicious && (
            <div className={clsx(ECON_GRID, ROW_CLS, 'bg-amber-500/5')}>
              <div className={LABEL_CLS} />
              <div className={VALUE_CLS} />
              <div className={UNIT_CLS} />
              <div className="px-2 flex items-center gap-2 text-[11px] text-amber-200">
                <AlertTriangle size={12} className="shrink-0 text-amber-300" />
                <span className="truncate">{fitOutSuspiciousText}</span>
              </div>
            </div>
          )}

          {stats.isRiskyTenant && (
            <div className={clsx(ECON_GRID, ROW_CLS, 'bg-rose-500/5')}>
              <div className={LABEL_CLS} />
              <div className={VALUE_CLS} />
              <div className={UNIT_CLS} />
              <div className="px-2 flex items-center gap-2 text-[11px] text-rose-200/80">
                <ShieldAlert size={12} className="shrink-0 text-rose-400" />
                <span className="truncate">{t.highRisk} {stats.tenantHealth}</span>
              </div>
            </div>
          )}

          {/* Progressive Rent */}
          <div className={clsx(ECON_GRID, ROW_CLS)}>
            <div className={LABEL_CLS}>{t.progressiveRent}</div>
            <div className={VALUE_CLS}>
              <Switch checked={hasStepRents} onCheckedChange={setHasStepRents} labelRight={null} />
            </div>
            <div className={UNIT_CLS} />
            <div className={DESC_CLS}>{lang === 'fr' ? 'Loyer progressif par paliers' : 'Stepped rent schedule'}</div>
          </div>

          {hasStepRents && (
            <>
              {steps.map((step, idx) => (
                // Index key: DealEconomicsStepDraft carries no stable identifier and the row holds no local state.
                <DealStepRow
                  key={idx}
                  index={idx}
                  startMonth={step.startMonth}
                  endMonth={step.endMonth}
                  rentPercent={step.rentPercent}
                  lang={lang}
                  onPatchStep={handlePatchStep}
                  onRemoveStep={handleRemoveStep}
                />
              ))}
              <div className="px-4 py-2 border-b border-[#2A2A2A]/50">
                <button onClick={handleAddStep} className="w-full h-8 border border-dashed border-white/10 rounded-md text-[11px] text-white/20 hover:text-white hover:bg-white/5 hover:border-white/30 transition-all">{t.addProgressionStep}</button>
              </div>
              {cliffWarnings.length > 0 && cliffWarnings.map((w, i) => (
                <div key={i} className={clsx(ECON_GRID, ROW_CLS, 'bg-rose-500/5')}>
                  <div className={LABEL_CLS} />
                  <div className={VALUE_CLS} />
                  <div className={UNIT_CLS} />
                  <div className="px-2 flex items-center gap-2 text-[11px] text-rose-200/80">
                    <AlertTriangle size={12} className="shrink-0 text-rose-300" />
                    <span className="truncate">{w}</span>
                  </div>
                </div>
              ))}
            </>
          )}

          {/* Computed KPIs, same row style */}
          <div className={clsx(!stats.isFoundationSet && 'opacity-20 pointer-events-none')}>
            <div className={clsx(ECON_GRID, ROW_CLS)}>
              <div className={LABEL_CLS}>{t.assetValuation}</div>
              <div className={clsx(VALUE_CLS, 'font-mono')}>€{(stats.assetValuation / 1000000).toFixed(2)}M</div>
              <div className={UNIT_CLS} />
              <div className={DESC_CLS}>{t.assumingExitYield(DEFAULT_CAP_RATE * 100)}</div>
            </div>
            <div className={clsx(ECON_GRID, ROW_CLS)}>
              <div className={LABEL_CLS}>{t.netEffectiveRent}</div>
              <div className={clsx(VALUE_CLS, 'font-mono')}>€{stats.netEffectiveAnnual.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
              <div className={UNIT_CLS}>
                <span className={stats.nerDiscountPercent > 0 ? 'text-rose-400' : ''}>-{stats.nerDiscountPercent.toFixed(1)}%</span>
              </div>
              <div className={DESC_CLS}>{lang === 'fr' ? 'Loyer net après incentives' : 'Net rent after incentives'}</div>
            </div>
            <div className={clsx(ECON_GRID, ROW_CLS)}>
              <div className={LABEL_CLS}>{t.totalIncentives}</div>
              <div className={clsx(VALUE_CLS, 'font-mono')}>€{stats.totalIncentiveValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
              <div className={UNIT_CLS} />
              <div className={DESC_CLS}>{t.capexOpexCombined}</div>
            </div>
            <div className={clsx(ECON_GRID, ROW_CLS, stats.isPaybackDangerous && 'bg-rose-500/5')}>
              <div className={LABEL_CLS}>{t.paybackPeriod}</div>
              <div className={clsx(VALUE_CLS, 'font-mono', stats.isPaybackDangerous && 'text-rose-400')}>
                {stats.paybackMonths.toFixed(1)}
              </div>
              <div className={UNIT_CLS}>{lang === 'fr' ? 'mois' : 'mo'}</div>
              <div className={DESC_CLS}>
                {stats.isPaybackDangerous
                  ? <span className="text-rose-400">{t.exceedsFirmPeriod}</span>
                  : lang === 'fr' ? 'Retour sur investissement' : 'Time to recoup incentives'}
              </div>
            </div>
            {hasStepRents && (
              <div className={clsx(ECON_GRID, ROW_CLS)}>
                <div className={LABEL_CLS}>{t.discountEquivalent}</div>
                <div className={clsx(VALUE_CLS, 'font-mono')}>
                  {(stats.monthlyRent > 0 ? (stats.totalIncentiveValue - stats.rfValue - fitOutAmount) / stats.monthlyRent : 0).toFixed(1)}
                </div>
                <div className={UNIT_CLS}>{lang === 'fr' ? 'mois' : 'mo'}</div>
                <div className={DESC_CLS}>{lang === 'fr' ? 'Équivalent en mois de loyer' : 'Equivalent in rent months'}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export type DealWorkspaceTab = 'ECONOMICS' | 'CONTACT' | 'DRAFTING' | 'DESIGNS' | 'UNIT';

export const resolveDealWorkspaceTabForAction = (
  actionType?: LeasingActionType
): DealWorkspaceTab => {
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- pre-existing in this file, inherited by the merge gate (it judges whole files); changing it alters behaviour outside this merge
  switch (actionType) {
    case 'REDACT_INCENTIVE_LETTER':
    case 'REDACT_LOI':
    case 'SELECT_LOI_TEMPLATE':
      return 'DRAFTING';
    case 'SELECT_DEAL_CONTACT':
      return 'CONTACT';
    default:
      return 'ECONOMICS';
  }
};

export const resolveDealEconomicsTabForAction = (
  actionType?: LeasingActionType
): DealEconomicsTab | null => {
  if (!actionType) return null;
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- pre-existing in this file, inherited by the merge gate (it judges whole files); changing it alters behaviour outside this merge
  switch (actionType) {
    case 'DECIDE_BASE_RENT':
    case 'DECIDE_LEASE_DURATION':
    case 'DECIDE_RENT_FREE_PERIOD':
    case 'VALIDATE_INCENTIVE_COVENANT':
    case 'SELECT_INCENTIVE_TEMPLATE':
      return 'RENT_FREE';
    case 'DECIDE_FITOUT_CONTRIBUTION':
    case 'ESTIMATE_TENANT_FITOUT_COST':
      return 'FITOUT';
    case 'DECIDE_STEP_RENTS':
      return 'STEPS';
    default:
      return null;
  }
};

export const sanitizeDealEconomicsTab = (tab: DealEconomicsTab): DealEconomicsTab =>
  tab === 'CONTACT' ? 'RENT_FREE' : tab;

interface DealPageProps {
  dealId: string;
  className?: string;
  initialWorkspaceTab?: DealWorkspaceTab;
  economicsInitialTab?: DealEconomicsTab;
  focusedActionType?: LeasingActionType;
  hasEntityRowAbove?: boolean;
}

const DEAL_PAGE_TABS = new Set<DealWorkspaceTab>(['CONTACT', 'ECONOMICS', 'DRAFTING', 'UNIT', 'DESIGNS']);
const DEAL_ECONOMICS_TABS = new Set<DealEconomicsTab>(['RENT_FREE', 'FITOUT', 'STEPS', 'CONTACT']);
const LEASING_ACTION_TYPE_SET = new Set<LeasingActionType>(LEASING_ACTION_TYPES);

const isLeasingActionType = (value: unknown): value is LeasingActionType =>
  typeof value === 'string' && LEASING_ACTION_TYPE_SET.has(value as LeasingActionType);

const normalizeDealWorkspaceTab = (value: unknown): DealWorkspaceTab | undefined =>
  typeof value === 'string' && DEAL_PAGE_TABS.has(value as DealWorkspaceTab)
    ? (value as DealWorkspaceTab)
    : undefined;

const normalizeDealEconomicsTab = (value: unknown): DealEconomicsTab | undefined =>
  typeof value === 'string' && DEAL_ECONOMICS_TABS.has(value as DealEconomicsTab)
    ? sanitizeDealEconomicsTab(value as DealEconomicsTab)
    : undefined;

const normalizeDealEntityActions = (value: unknown) => {
  if (!Array.isArray(value)) return [];
  return value.reduce<Array<{ type: LeasingActionType; entityType?: string; entityId?: string }>>((acc, candidate) => {
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return acc;
    const candidateRecord = candidate as Record<string, unknown>;
    if (!isLeasingActionType(candidateRecord.type)) return acc;
    acc.push({
      type: candidateRecord.type,
      entityType: typeof candidateRecord.entityType === 'string' ? candidateRecord.entityType : undefined,
      entityId: typeof candidateRecord.entityId === 'string' ? candidateRecord.entityId : undefined,
    });
    return acc;
  }, []);
};

export const DealPage = ({
  dealId,
  className,
  initialWorkspaceTab,
  economicsInitialTab = 'RENT_FREE',
  focusedActionType,
  hasEntityRowAbove = false,
}: DealPageProps) => {
  const { kernel } = useKernel();
  const lang = useKernelStore((state) => state.language);
  const activeChatSessionId = useKernelStore((state) => state.activeChatSessionId);
  const activeSessionSubject = useKernelStore((state) =>
    activeChatSessionId ? state.inventory.ChatSession.registry[activeChatSessionId]?.activeSubject ?? null : null
  );
  const deal = useKernelStore(
    (state) => (state.inventory.Deal.registry[dealId] as DealInventoryEntityState | undefined) ?? null
  );
  const linkedUnit = useKernelStore((state) => (deal?.unitId ? state.inventory.Unit.registry[deal.unitId] : null));

  const effectiveFocusedActionType = useMemo<LeasingActionType | undefined>(() => {
    if (activeSessionSubject?.entityType === 'Deal' && activeSessionSubject.entityId === dealId) {
      return activeSessionSubject.focusActionType;
    }
    return focusedActionType;
  }, [activeSessionSubject, dealId, focusedActionType]);

  const fallbackWorkspaceTab = useMemo<DealWorkspaceTab>(() => {
    if (initialWorkspaceTab) return initialWorkspaceTab;
    if (effectiveFocusedActionType) return resolveDealWorkspaceTabForAction(effectiveFocusedActionType);
    return 'CONTACT';
  }, [initialWorkspaceTab, effectiveFocusedActionType]);

  const fallbackEconomicsTab = useMemo<DealEconomicsTab>(() => {
    const fromAction = resolveDealEconomicsTabForAction(effectiveFocusedActionType);
    return sanitizeDealEconomicsTab(fromAction ?? economicsInitialTab);
  }, [effectiveFocusedActionType, economicsInitialTab]);

  const activeTabCandidate = deal?.activeTab ?? deal?.runtime?.activeTab;
  const activeEconomicsTabCandidate = deal?.activeEconomicsTab ?? deal?.runtime?.activeEconomicsTab;
  const lastFocusedActionType = isLeasingActionType(
    deal?.lastFocusedActionType ?? deal?.runtime?.lastFocusedActionType
  )
    ? (deal?.lastFocusedActionType ?? deal?.runtime?.lastFocusedActionType)
    : undefined;
  const activeTab = normalizeDealWorkspaceTab(activeTabCandidate) ?? fallbackWorkspaceTab;
  const activeEconomicsTab = normalizeDealEconomicsTab(activeEconomicsTabCandidate) ?? fallbackEconomicsTab;

  const patchKernelState = useCallback(
    (
      patch: Partial<
        Pick<DealRuntimeState, 'activeTab' | 'activeEconomicsTab' | 'lastFocusedActionType' | 'entityActions'>
      >
    ) => {
      if (!kernel.current) return;
      kernel.current.patchEntityRuntimeState('Deal', dealId, patch);
    },
    [kernel, dealId]
  );

  useEffect(() => {
    if (!kernel.current) return;
    if (normalizeDealWorkspaceTab(activeTabCandidate) && normalizeDealEconomicsTab(activeEconomicsTabCandidate)) return;
    kernel.current.patchEntityRuntimeState(
      'Deal',
      dealId,
      {
        activeTab: fallbackWorkspaceTab,
        activeEconomicsTab: fallbackEconomicsTab,
        lastFocusedActionType: effectiveFocusedActionType,
      }
    );
  }, [kernel, dealId, activeTabCandidate, activeEconomicsTabCandidate, fallbackWorkspaceTab, fallbackEconomicsTab, effectiveFocusedActionType]);

  useEffect(() => {
    if (!effectiveFocusedActionType) return;
    const alreadyApplied = lastFocusedActionType === effectiveFocusedActionType;
    if (alreadyApplied) return;
    const nextWorkspaceTab = resolveDealWorkspaceTabForAction(effectiveFocusedActionType);
    const nextEconomicsTab = resolveDealEconomicsTabForAction(effectiveFocusedActionType);
    patchKernelState({
      activeTab: nextWorkspaceTab,
      ...(nextEconomicsTab ? { activeEconomicsTab: sanitizeDealEconomicsTab(nextEconomicsTab) } : {}),
      lastFocusedActionType: effectiveFocusedActionType,
    });
  }, [
    effectiveFocusedActionType,
    lastFocusedActionType,
    patchKernelState,
  ]);

  useEffect(() => {
    if (!kernel.current || !dealId) return;
    void kernel.current.syncEntity('Deal', dealId);
  }, [kernel, dealId]);

  const actionChoices = useMemo(
    () =>
      normalizeDealEntityActions(deal?.entityActions ?? deal?.runtime?.entityActions).filter(
        (action) => action.entityType === 'Deal' && action.entityId === dealId
      ),
    [deal?.entityActions, deal?.runtime?.entityActions, dealId]
  );

  const highlightedMainTabs = useMemo(() => {
    const tabs = new Set<DealWorkspaceTab>();
    for (const action of actionChoices) {
      tabs.add(resolveDealWorkspaceTabForAction(action.type));
    }
    return tabs;
  }, [actionChoices]);

  const highlightedDealActionTypes = useMemo(
    () => Array.from(new Set(actionChoices.map((action) => action.type))),
    [actionChoices]
  );

  const tabs = useMemo(
    () => [
      { id: 'CONTACT' as const, label: lang === 'fr' ? 'Contact' : 'Contact', hasPendingIndicator: highlightedMainTabs.has('CONTACT') },
      { id: 'ECONOMICS' as const, label: lang === 'fr' ? 'Economie' : 'Economics', hasPendingIndicator: highlightedMainTabs.has('ECONOMICS') },
      { id: 'DRAFTING' as const, label: lang === 'fr' ? 'Documents' : 'Drafting', hasPendingIndicator: highlightedMainTabs.has('DRAFTING') },
      { id: 'UNIT' as const, label: lang === 'fr' ? 'Unite' : 'Unit' },
      { id: 'DESIGNS' as const, label: lang === 'fr' ? 'Designs' : 'Designs' },
    ],
    [lang, highlightedMainTabs]
  );

  const onTabChange = useCallback(
    (nextTab: DealWorkspaceTab) => {
      patchKernelState({ activeTab: nextTab });
    },
    [patchKernelState]
  );

  return (
    <div className={clsx('flex flex-col h-full bg-[#141414] text-white', className)}>
      <EntityPageTabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={onTabChange}
        sticky
      />

      <div className="flex-1 overflow-y-auto">
        {activeTab === 'CONTACT' && (
          <DealContactTab
            dealId={dealId}
            highlightedActionTypes={highlightedDealActionTypes}
          />
        )}
        {activeTab === 'ECONOMICS' && (
          <DealEconomicsTabPanel
            dealId={dealId}
            initialTab={activeEconomicsTab}
            highlightedActionTypes={highlightedDealActionTypes}
          />
        )}
        {activeTab === 'DRAFTING' && (
          <GenerateIncentiveLetterActionPanel
            dealId={dealId}
            highlightedActionTypes={highlightedDealActionTypes}
            focusedActionType={effectiveFocusedActionType}
          />
        )}
        {activeTab === 'UNIT' &&
          (!deal ? (
            <EntitySkeleton variant="detail" />
          ) : deal.unitId ? (
            <UnitPage unitId={deal.unitId} className="h-full" />
          ) : (
            <div className="flex h-full items-center justify-center text-[12px] text-white/35">
              {lang === 'fr' ? 'Aucune unite liee a ce deal.' : 'No unit is linked to this deal yet.'}
            </div>
          ))}
        {activeTab === 'DESIGNS' && <DealDesignsTab dealId={dealId} />}
      </div>
    </div>
  );
};
